import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import {
  IAppsFields,
  ICategoriesFields,
  ITypesFields,
} from "../types/appstore";
import { IMenuLinksFields } from "types/contentful";
import {
  getAppById,
  getApps,
  getAppsSettingsAndInstallations,
  getCategories,
  getTypes,
} from "./appStoreApi";
import { useLocalStorageCache } from "./utils/useLocalStorageCache";
import { useContext } from "react";
import { AppContext } from "../context/app";
import {
  applyIsInstalledFlag,
  filterAppRules,
  orderApps,
} from "./appStore.utils";

const cacheDuration = 20 * 60 * 60 * 1000; // 20 hours in milliseconds

export function useAppBySlug(
  slug: string,
  options?: UseQueryOptions<IAppsFields>,
) {
  const { client } = useContext(AppContext);
  const clientId = client?.uuid || client?.client_uuid;

  return useQuery<IAppsFields>({
    ...options,
    queryKey: ["get-app-by-slug", slug],
    queryFn: async () => {
      const [{ settings, installations }, app] = await Promise.all([
        getAppsSettingsAndInstallations(clientId),
        getAppById(slug),
      ]);

      const apps = applyIsInstalledFlag({
        apps: [app],
        settings,
        installations,
      });

      return apps[0];
    },
  });
}

export function useAppStoreAppsCategoriesAndTypes(
  options?: UseQueryOptions<{
    apps: IAppsFields[];
    categories: ICategoriesFields[];
    types: ITypesFields[];
  }>,
) {
  const { client } = useContext(AppContext);
  const clientId = client?.uuid || client?.client_uuid;

  return useQuery<{
    apps: IAppsFields[];
    categories: ICategoriesFields[];
    types: ITypesFields[];
  }>(
    ["app-store-page", clientId],
    async () => {
      const [{ settings, installations }, apps, categories, types] =
        await Promise.all([
          getAppsSettingsAndInstallations(clientId),
          getApps(),
          getCategories(),
          getTypes(),
        ]);

      const filteredApps = filterAppRules(apps, client);

      const modifiedApps = applyIsInstalledFlag({
        apps: filteredApps,
        settings,
        installations,
      });

      const orderedApps = orderApps(modifiedApps);

      return {
        apps: orderedApps || [],
        categories,
        types,
      };
    },
    options,
  );
}

export function useAppStore() {
  return useLocalStorageCache({
    queryKey: "app-store-1", // increment to avoid getting old data
    queryFn: getApps,
    cacheDuration,
  });
}

export const useAppStoreMenuLinks = (
  apps: IAppsFields[],
  clientId: string,
  options?: UseQueryOptions<IMenuLinksFields[]>,
) => {
  return useQuery<IMenuLinksFields[]>(
    ["app-store-menu-links", clientId],
    async () => {
      const { settings, installations } =
        await getAppsSettingsAndInstallations(clientId);

      return applyIsInstalledFlag({
        apps,
        settings,
        installations,
      })
        .filter((app) => (app.isInstalled && app.menuLinks?.length) ?? 0 > 0)
        .map((app) => app.menuLinks as IMenuLinksFields[])
        .flat()
        .sort((a, b) => a?.name?.localeCompare(b?.name));
    },
    options,
  );
};

export const useAppsSettingsAndInstallations = () => {
  const { client } = useContext(AppContext);
  const clientId = client?.uuid || client?.client_uuid;

  return useQuery({
    queryKey: ["settings-and-installations", clientId],
    queryFn: () => getAppsSettingsAndInstallations(clientId),
  });
};

/**
 * Hook to get a specific app setting's value
 * @param type The settings type to filter by
 * @param settingName The name of the setting
 * @returns The boolean value of the setting, or null if the setting is not found
 */
export const useAppSetting = (type: string, settingName: string) => {
  const { data, isLoading, error } = useAppsSettingsAndInstallations();
  const rawSettingValue = data?.settings.find(
    (s) => s.type === type && s.name === settingName,
  )?.value;
  const value =
    rawSettingValue === null || rawSettingValue === undefined
      ? null
      : rawSettingValue === "1";

  return {
    value,
    isLoading,
    error,
  };
};
